import { differenceInSeconds } from 'date-fns'
import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { LOG_GROUP_ATTRIBUTE_NAME } from 'constants/index'
import useApp from 'hooks/redux/useApp'
import useAppConfig from 'hooks/redux/useAppConfig'
import useSettings from 'hooks/redux/useSettings'
import { setACWDuration } from 'store/acw/acw.actions'
import { selectACW } from 'store/acw/acw.selectors'
import { IACWContact } from 'store/acw/acw.state'
import { completeCallACW as clearCallContact } from 'store/call/call.actions'
import { addToCallLog } from 'store/callLog/callLog.actions'
import { completeChatACW } from 'store/chat/chat.actions'
import { completeCallACW as markCallACWComplete } from 'store/contact/contact.actions'
import RootState from 'store/state'
import { completeTaskACW } from 'store/tasks/tasks.actions'
import { setState } from 'store/user/user.actions'
import { getFormName } from 'utils'

import { Channel } from '@aws-sdk/client-connect'

const DEFAULT_ACW_TIME_SECONDS = 60
const ACW_TICK_MS = 1000


const ACWTimer: React.FC = () => {
    const dispatch = useDispatch()

    const acwContacts = useSelector(selectACW)
    const chatConnections = useSelector((state: RootState) => state.chat.connections)
    const taskConnections = useSelector((state: RootState) => state.tasks.connections)
    const contact = useSelector((state: RootState) => state.contact)
    const contacts = useSelector((state: RootState) => state.contacts)
    const nextAgentStatus = useSelector((state: RootState) => state.user?.afterCallStatus)
    const otherContacts = useSelector((state: RootState) =>
        state.contacts.some((c) => c.channel !== Channel.VOICE),
    )

    const app = useApp()
    const { settings } = useSettings()
    const { chatAfterCallWorkInitialTimeoutSeconds, afterCallWorkInitialTimeoutSeconds } =
        useAppConfig()

    const contactLogForm =
        app?.forms?.[getFormName(contact?.attributes?.[LOG_GROUP_ATTRIBUTE_NAME], app)]

    const acwList = useMemo(() => Object.values(acwContacts), [acwContacts])
    const isWrappingUp = acwList.length > 0

    const getChannel = (acw: IACWContact) => {
        switch (acw.type) {
            case 'voice':
                return 'VOICE'
            case 'chat':
                return chatConnections.find((c) => c.id === acw.contactID)?.channel ?? 'CHAT'
            default:
                return taskConnections.find((t) => t.id === acw.contactID)?.channel ?? 'EMAIL'
        }
    }

    const getDurationSeconds = (acw: IACWContact) => {
        if (acw.contactID === contact?.ID && contactLogForm?.form?.wrapTimeSeconds) {
            return contactLogForm.form.wrapTimeSeconds
        }

        switch (getChannel(acw)) {
            case 'CHAT':
                return (
                    settings?.appConfigurations?.chat?.afterCallWorkTimeoutSeconds ??
                    chatAfterCallWorkInitialTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            case 'EMAIL':
            case 'WEBFORM':
                return (
                    settings?.appConfigurations?.task?.afterCallWorkTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            case 'VOICE':
                return (
                    settings?.appConfigurations?.afterCallWorkTimeoutSeconds ??
                    afterCallWorkInitialTimeoutSeconds ??
                    DEFAULT_ACW_TIME_SECONDS
                )
            default:
                return DEFAULT_ACW_TIME_SECONDS
        }
    }

    const completeACW = (acw: IACWContact) => {
        switch (acw.type) {
            case 'chat':
                dispatch(completeChatACW(acw.contactID))
                return
            case 'task': {
                const taskContact =
                    contact?.ID === acw.contactID
                        ? contact
                        : contacts.find((c) => c.ID === acw.contactID)

                dispatch(completeTaskACW(acw.contactID))
                taskContact && dispatch(addToCallLog(taskContact))
                return
            }
            default:
                nextAgentStatus && !otherContacts && dispatch(setState(nextAgentStatus))
                dispatch(markCallACWComplete(acw.contactID))
                dispatch(clearCallContact())
        }
    }

    useEffect(() => {
        acwList
            .filter((acw) => acw.durationSeconds === undefined)
            .forEach((acw) =>
                dispatch(
                    setACWDuration({
                        contactID: acw.contactID,
                        durationSeconds: getDurationSeconds(acw),
                    }),
                ),
            )
    }, [acwList, contactLogForm, settings, app?.appConfig])

    const isLimited = (acw: IACWContact) => {
        if (acw.type === 'chat') {
            return settings?.appConfigurations?.chat?.isAfterCallWorkLimited !== false
        }

        switch (getChannel(acw)) {
            case 'EMAIL':
            case 'WEBFORM':
                return !!settings?.appConfigurations?.task?.isAfterCallWorkLimited
            case 'VOICE':
                return !!settings?.appConfigurations?.isAfterCallWorkLimited
            default:
                return false
        }
    }

    const tick = () => {
        acwList.forEach((acw) => {
            if (acw.extendedAt || acw.durationSeconds === undefined) return
            if (!isLimited(acw) || acw.durationSeconds <= 0) return
            if (differenceInSeconds(Date.now(), acw.startedAt) < acw.durationSeconds) return

            completeACW(acw)
        })
    }

    const latestTick = useRef(tick)
    useEffect(() => {
        latestTick.current = tick
    })

    useEffect(() => {
        if (!isWrappingUp) return

        const interval = window.setInterval(() => latestTick.current(), ACW_TICK_MS)

        return () => clearInterval(interval)
    }, [isWrappingUp])

    return null
}

export default ACWTimer
